import React, { useCallback, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  Button,
  Col,
  Divider,
  Empty,
  Flex,
  Input,
  message,
  Row,
  Select,
  Space,
  Tag,
  Typography
} from "antd";
import { debounce } from "lodash";
import {
  apiGetMySavedWholesaleSuppliers,
  apiSearchWholesalesSupplier,
  apiUpdateMySavedWholesaleSuppliers
} from "api/wholesales";
import { connectAutoDispatch } from "../../../../redux/reduxConnect";
import { compose } from "redux";

const LOCATIONS = [
  "US",
  "Canada",
  "United Kingdom",
  "Germany",
  "Italy",
  "Spain",
  "France",
  "Poland",
  "Latvia",
  "Switzerland"
];
const APPROVED_FORS = ["Online Store", "Amazon", "eBay", "Physical Retail"];
const WHOLESALE_TYPES = ["Light Bulk", "Dropship", "Private Label", "Pallet Deals"];
const CATEGORY_LIST = [
  "Art",
  "Baby & Maternity",
  "Books, Movies & Media",
  "Business & Industrial",
  "Clothing, Shoes & Accessories",
  "Collectibles",
  "Computers & Networking",
  "Consumer Electronics",
  "Crafts & Supplies",
  "Food & Drinks",
  "Health & Beauty",
  "Home & Garden",
  "Jewelry & Watches",
  "Mature Audiences 18+",
  "Music & Instruments",
  "Office & School Supplies",
  "Pet Supplies",
  "Special Occasions & Party",
  "Sporting Goods",
  "Supplements & Nutrition",
  "Tools & Hardware",
  "Toys, Games & Hobbies",
  "Travel",
  "Vehicles, Parts & Accessories"
];

const SearchWholesaleSuppliers = ({ userState }) => {
  const {
    userInfo: { isInTrial, productType }
  } = userState;

  if (
    (!isInTrial && !["basic", "hazmat"].includes(productType)) ||
    isInTrial ||
    productType === "hazmat"
  ) {
    return (
      <Empty
        description={
          <Typography.Text strong>
            Subscribe to AZInsight Advanced plan now to access the wholesale suppliers list.
          </Typography.Text>
        }
      />
    );
  }

  const gridRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [mySavedList, setMySavedList] = useState([]);
  const mySavedListRef = useRef(mySavedList);

  const [columnDefs, setColumnDefs] = useState([
    {
      headerName: "",
      field: "_id",
      hide: true
    },
    {
      headerName: "Location",
      field: "location",
      pinned: "left",
      width: 100
    },
    {
      headerName: "Category",
      field: "category",
      pinned: "left",
      width: 100
    },
    {
      headerName: "Company",
      field: "company",
      pinned: "left",
      width: 200
    },
    {
      headerName: "Address",
      field: "address",
      width: 200
    },
    {
      headerName: "Website",
      field: "site",
      width: 200,
      cellRenderer: params => {
        if (!params.value) return null;
        let url = params.value;
        try {
          url = new URL(params.value).href;
        } catch (e) {
          url = `https://${params.value}`;
        }
        return (
          <a href={url} target="_blank" rel="noreferrer">
            {params.value}
          </a>
        );
      }
    },
    {
      headerName: "Description",
      field: "description",
      width: 200
    },
    {
      headerName: "Wholesale Type",
      field: "wholesaleType",
      width: 200,
      valueFormatter: params => (params.value || []).join(", ")
    },
    {
      headerName: "Approved For",
      field: "approvedFor",
      width: 200,
      valueFormatter: params => (params.value || []).join(", ")
    },
    {
      headerName: "Brands",
      field: "brandNames",
      width: 200,
      valueFormatter: params => (params.value || []).join(", ")
    },
    {
      headerName: "Product Types",
      field: "productTypes",
      width: 200,
      valueFormatter: params => (params.value || []).join(", ")
    },
    {
      headerName: "Actions",
      field: "action",
      pinned: "right",
      cellRenderer: params => {
        const supplier = params.data;
        const isSaved = supplier.saved;
        return (
          <Button onClick={() => handleOnSave(supplier._id)} disabled={isSaved} loading={loading}>
            {isSaved ? "Saved" : "Save"}
          </Button>
        );
      }
    }
  ]);

  const initialState = JSON.parse(localStorage.getItem("wholesaleFilters")) || {
    company: null,
    category: null,
    locations: [],
    approvedFors: [],
    wholesaleTypes: []
  };

  const [state, setState] = useState(initialState);

  const limit = 100;

  const getRowId = params => params.data._id;

  const [gridOptions, setGridOptions] = useState({
    rowModelType: "serverSide",
    rowSelection: "multiple",
    defaultColDef: {
      sortable: true,
      resizable: true,
      filter: true
    },
    isExternalFilterPresent: () => true,
    doesExternalFilterPass: () => true,
    domLayout: "autoHeight",
    cacheBlockSize: limit,
    overlayNoRowsTemplate: "No results found with current filters",
    suppressHorizontalScroll: false,
    suppressContextMenu: true
  });

  const onGridReady = params => {
    gridRef.current.api.setColumnDefs(columnDefs);
    gridRef.current.api.closeToolPanel();
    onGetList();
  };

  const onGetList = () => {
    const emptyState = Object.keys(state).every(key => {
      if (Array.isArray(state[key])) {
        return state[key].length === 0;
      }
      return !state[key] || state[key] === "";
    });

    if (emptyState) {
      const emptyDataSource = {
        getRows: params => {
          params.successCallback([], 0);
          gridRef.current.api.showNoRowsOverlay();
        }
      };
      gridRef.current.api.setServerSideDatasource(emptyDataSource);
    } else {
      const dataSource = getServerSideDatasource();
      gridRef.current.api.setServerSideDatasource(dataSource);
    }
  };

  const getServerSideDatasource = useCallback(() => {
    return {
      getRows: debounce(params => {
        const { company, category, locations, approvedFors, wholesaleTypes } = state;
        const { startRow, sortModel, filterModel } = params.request;
        const page =
          startRow === 0 || startRow === undefined ? 1 : Math.floor(startRow / limit) + 1;

        apiSearchWholesalesSupplier({
          page,
          limit,
          company,
          option: {
            category,
            location: locations,
            approvedFor: approvedFors,
            wholesaleType: wholesaleTypes
          }
        })
          .then(resp => {
            const suppliers = resp?.data?.suppliers || [];
            if (!Array.isArray(suppliers)) {
              params.fail();
              gridRef.current.api.showNoRowsOverlay();
              return;
            }

            const resultTotal = resp?.data?.total;

            gridRef.current.api.hideOverlay();

            const newSuppliers = suppliers.map(supplier => {
              const isSaved = mySavedListRef.current.some(
                saved => saved.wholesaleId === supplier._id
              );
              return {
                ...supplier,
                saved: isSaved
              };
            });

            params.successCallback(newSuppliers, resultTotal);
          })
          .catch(error => {
            console.error("API Error:", error);
            params.fail();
            gridRef.current.api.showNoRowsOverlay();
          });
      }, 500)
    };
  }, [state]);

  const onSearch = value =>
    setState(prev => {
      const newState = { ...prev, company: value };
      localStorage.setItem("wholesaleFilters", JSON.stringify(newState));
      return newState;
    });

  const handleSelectTags = (key, tag, checked) => {
    const nextState = checked ? [...state[key], tag] : state[key].filter(t => t !== tag);
    setState(prev => {
      const newState = { ...prev, [key]: nextState };
      localStorage.setItem("wholesaleFilters", JSON.stringify(newState));
      return newState;
    });
  };

  const onSelectCategory = value => {
    setState(prev => {
      const newState = { ...prev, category: value };
      localStorage.setItem("wholesaleFilters", JSON.stringify(newState));
      return newState;
    });
  };

  const onTagsSelection = (key, value) => {
    setState(prev => {
      const newState = { ...prev, [key]: value };
      localStorage.setItem("wholesaleFilters", JSON.stringify(newState));
      return newState;
    });
  };

  const onSearchCategory = value => {
    setState(prev => {
      const newState = { ...prev, category: value };
      localStorage.setItem("wholesaleFilters", JSON.stringify(newState));
      return newState;
    });
  };

  const handleOnSave = supplierId => {
    setLoading(true);
    apiUpdateMySavedWholesaleSuppliers({
      id: "",
      wholesaleId: supplierId,
      myNote: ""
    })
      .then(() => {
        return fetchMySaved();
      })
      .then(() => {
        message.success("Supplier saved successfully");
        console.log(state);
        onGetList();
      })
      .catch(() => message.error("Cannot save, please try again"))
      .finally(() => setLoading(false));
  };

  const fetchMySaved = () => {
    setLoading(true);
    return apiGetMySavedWholesaleSuppliers({
      page: 1,
      limit: 100
    })
      .then(({ data }) => {
        setMySavedList(data?.suppliers || []);
      })
      .catch(() => message.error("Cannot get my saved list, please try again"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      onGetList();
    }
  }, [state]);

  useEffect(() => {
    fetchMySaved();
  }, []);

  useEffect(() => {
    mySavedListRef.current = mySavedList;
  }, [mySavedList]);

  return (
    <div>
      <Typography.Title>Wholesale Suppliers</Typography.Title>
      <Space direction="vertical" style={{ width: "100%", marginBottom: 20 }}>
        <Input.Search
          allowClear
          placeholder="search for company"
          onSearch={onSearch}
          style={{ width: 200 }}
        />
        <Divider orientation="left" plain>
          Advanced Filter
        </Divider>
        <Row justify="start">
          <Col span={4}>
            <Space direction="vertical">
              <Typography.Title level={5}>Location</Typography.Title>
              <Select
                mode="tags"
                popupMatchSelectWidth={false}
                placeholder="Select Location(s)"
                optionFilterProp="label"
                onChange={(value) => onTagsSelection("locations", value)}
                value={state.locations}
                options={LOCATIONS.map(location => ({
                  value: location,
                  label: location
                }))}
                style={{
                  width: 175
                }}
                dropdownStyle={{
                  width: 250
                }}
              />
            </Space>
          </Col>
          <Col span={4}>
            <Space direction="vertical">
              <Typography.Title level={5}>Approved For</Typography.Title>
              {APPROVED_FORS.map(approvedFor => (
                <Tag.CheckableTag
                  key={approvedFor}
                  checked={state.approvedFors.includes(approvedFor)}
                  onChange={checked => handleSelectTags("approvedFors", approvedFor, checked)}
                >
                  {approvedFor}
                </Tag.CheckableTag>
              ))}
            </Space>
          </Col>
          <Col span={4}>
            <Space direction="vertical">
              <Typography.Title level={5}>Wholesale Type</Typography.Title>
              {WHOLESALE_TYPES.map(wholesaleType => (
                <Tag.CheckableTag
                  key={wholesaleType}
                  checked={state.wholesaleTypes.includes(wholesaleType)}
                  onChange={checked => handleSelectTags("wholesaleTypes", wholesaleType, checked)}
                >
                  {wholesaleType}
                </Tag.CheckableTag>
              ))}
            </Space>
          </Col>
          <Col span={4}>
            <Space direction="vertical">
              <Typography.Title level={5}>Category</Typography.Title>
              <Select
                showSearch
                allowClear
                popupMatchSelectWidth={false}
                placeholder="Select a category"
                optionFilterProp="label"
                onChange={onSelectCategory}
                onSearch={onSearchCategory}
                value={state.category}
                options={CATEGORY_LIST.map(category => ({
                  value: category,
                  label: category
                }))}
                style={{
                  width: "100%"
                }}
                dropdownStyle={{
                  width: 250
                }}
              />
            </Space>
          </Col>
        </Row>
      </Space>
      <div className="ag-theme-alpine" style={{ width: "100%", marginBottom: 50 }}>
        <AgGridReact
          ref={gridRef}
          style={{ width: "100%", height: "100%" }}
          animateRows
          enableRangeSelection
          getRowId={getRowId}
          gridOptions={gridOptions}
          onGridReady={onGridReady}
          pagination
          paginationPageSize={limit}
        />
      </div>
    </div>
  );
};

const withConnect = connectAutoDispatch(
  ({ userState }) => ({
    userState
  }),
  {}
);

export default compose(withConnect)(SearchWholesaleSuppliers);
